import type { Status } from '@artifax/shared';
import { OVERALL_COPY, STATUS_COLOR, STATUS_INK } from '../constants.js';
import { PulseDot } from './PulseDot.js';

/** Status-tinted background/border for the banner card. */
const TINT: Record<Status, { bg: string; border: string }> = {
  operational: { bg: 'rgba(24,147,90,.08)', border: 'rgba(24,147,90,.22)' },
  degraded: { bg: 'rgba(201,138,18,.10)', border: 'rgba(201,138,18,.30)' },
  partial: { bg: 'rgba(224,101,28,.10)', border: 'rgba(224,101,28,.30)' },
  major: { bg: 'rgba(207,58,58,.10)', border: 'rgba(207,58,58,.30)' },
  maintenance: { bg: 'rgba(58,111,208,.10)', border: 'rgba(58,111,208,.30)' },
};

interface Props {
  /** 'live' = fresh data; 'lost' = monitoring unreachable / data may be stale. */
  connectivity: 'live' | 'lost';
  overall: Status | null;
  updatedText: string | null;
}

export function OverallBanner({ connectivity, overall, updatedText }: Props) {
  // Connection lost: don't assert a status — tell the user reporting is paused.
  if (connectivity === 'lost' || !overall) {
    return (
      <section className="overall overall--lost" role="status" aria-live="polite">
        <div className="overall__glyph overall__glyph--lost" aria-hidden="true">
          !
        </div>
        <div className="overall__body">
          <h1 className="overall__title">Status reporting temporarily unavailable</h1>
          <p className="overall__sub">
            We’ve lost connection to our monitoring service, so live status can’t be shown right
            now.{' '}
            {updatedText ? `Information below may be out of date (${updatedText}).` : 'Please check back shortly.'}
          </p>
        </div>
        <div className="overall__updated">
          <PulseDot off />
          <span>Offline</span>
        </div>
      </section>
    );
  }

  const copy = OVERALL_COPY[overall];
  const tint = TINT[overall];
  return (
    <section
      className="overall"
      role="status"
      aria-live="polite"
      style={{ background: tint.bg, borderColor: tint.border }}
    >
      <div className="overall__glyph" style={{ background: STATUS_COLOR[overall] }} aria-hidden="true">
        {copy.glyph}
      </div>
      <div className="overall__body">
        <h1 className="overall__title" style={{ color: STATUS_INK[overall] }}>
          {copy.title}
        </h1>
        <p className="overall__sub">{copy.sub}</p>
      </div>
      <div className="overall__updated">
        <PulseDot large />
        <span>Live · {updatedText}</span>
      </div>
    </section>
  );
}
